/**
 * One shared document, live, inside something that is not a document route.
 *
 * Built for feedback requests: "which of these three mockups do you prefer" is
 * a visual question, and the browser answered it with grey letter-boxes because
 * it had no way to paint an artifact outside the docs route.
 *
 * What makes this cheap enough to mount several of is what it *doesn't* do:
 *
 * - **No comments.** `comments` is omitted rather than stubbed, so no comment
 *   room is opened. `ExtensionEditorMount` already treats absence as the honest
 *   answer and the extension hides the affordance instead of simulating one.
 * - **No page chrome.** `presentation="inline"` drops the status pill and
 *   collapses every failure to one line, because a "too large to edit in a
 *   browser" panel is longer than the box it would appear in.
 *
 * The editor-kind dispatch is `DocumentEditorSurface`, unchanged and shared
 * with the docs route. A preview that resolved editors differently from the
 * route would eventually paint a mockup as rich text, which is exactly the bug
 * that dispatch was written to prevent.
 */

import { useMemo } from 'react';
import {
  asTeamDocumentId,
  asTeamProjectId,
  type EditorViewport,
} from '@nimbalyst/collab-bundle/editor';

import type { TeamJwt, TeamSession } from '../auth/teamJwt';
import { consoleConfig } from '../config';
import { DocumentEditorSurface, useOrgRoster } from '../routes/DocsRoutes';
import { buildDocumentDesktopLink } from './documentDesktopLink';
import { useSharedDocumentType } from './sharedDocumentType';

export interface InlineCollabDocumentProps {
  /**
   * The project the document lives in. Resolved by the caller, which must
   * refuse rather than guess when a legacy ref carries no project id and the
   * org has several -- routing a document id through the wrong room is how a
   * preview silently shows nothing.
   */
  projectId: string;
  documentId: string;
  /** Only who is connecting; an embed in a page names them from its room. */
  teamSession: Pick<TeamSession, 'orgId' | 'memberId'>;
  getTeamJwt: (options?: { forceRefresh?: boolean }) => Promise<TeamJwt>;
  /**
   * Host-imposed read-only. A feedback preview leaves it unset; a canvas card
   * uses it to mount warm read-only and flip to editable on activation, without
   * remounting the editor or reconnecting its room.
   */
  readOnly?: boolean;
  /** Forwarded to an extension editor that publishes a scroll viewport. */
  onViewportRegistered?: (viewport: EditorViewport | null) => void;
}

export function InlineCollabDocument({
  projectId,
  documentId,
  teamSession,
  getTeamJwt,
  readOnly,
  onViewportRegistered,
}: InlineCollabDocumentProps) {
  const source = useMemo(() => ({
    kind: 'team-room' as const,
    serverUrl: consoleConfig.collabWebSocketBaseUrl,
    room: {
      // The session's branded org, not the artifact's plain string: the
      // feedback request and its artifacts are in one org by construction, and
      // taking it from the authenticated session is what makes that a type
      // rather than an assumption.
      orgId: teamSession.orgId,
      projectId: asTeamProjectId(projectId),
      documentId: asTeamDocumentId(documentId),
    },
    auth: {
      scope: 'team' as const,
      memberId: teamSession.memberId,
      getTeamJwt,
    },
  }), [documentId, getTeamJwt, projectId, teamSession.memberId, teamSession.orgId]);

  // This tab may show no presence, but everyone else in the room draws this
  // name on our cursor. The roster is one shared request per org, not one per
  // preview.
  const { members } = useOrgRoster(teamSession.orgId, getTeamJwt);
  const self = members.find((member) => member.memberId === teamSession.memberId);
  const user = useMemo(
    () => ({ memberId: teamSession.memberId, name: self?.name, email: self?.email }),
    [teamSession.memberId, self?.name, self?.email],
  );

  const resolution = useSharedDocumentType(teamSession.orgId, projectId, documentId);

  return (
    <DocumentEditorSurface
      resolution={resolution}
      source={source}
      user={user}
      // Omitted, not stubbed. See the header.
      comments={undefined}
      desktopHref={buildDocumentDesktopLink(documentId, teamSession.orgId, '', projectId)}
      presentation="inline"
      readOnly={readOnly}
      onViewportRegistered={onViewportRegistered}
    />
  );
}
